'use client';

import React from 'react';
import styles from './UniqueSection.module.scss';

import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from "@/src/components/ui/label/Label";
import NextImage from "@/src/components/ui/nextImage/NextImage";

const heading = 'Visual Storytelling Sections that Convert';
const excerpt =
    'Choose from a curated mix of visually engaging sections that showcase your products and drive conversions.';

type UniqueItemProps = {
    image: string;
    video?: string;
    alt: string;
    title: string;
    text: string;
};

const items: UniqueItemProps[] = [
    {
        image: '/images/themes/stockist/unique/pic-1.webp',
        alt: 'Reveal Collections on Hover',
        title: 'Reveal Collections on Hover',
        text: 'Turn browsing into an experience — hover effects on mouseover highlight collections in a more engaging way.',
    },
    {
        image: '/images/themes/stockist/unique/pic-2.webp',
        alt: 'Click-to-Transition Effect',
        title: 'Click-to-Transition Effect',
        text: 'Enjoy an elegant transition effect — collection names transform into full visuals on click, adding interaction and style to your layout.',
    },
    {
        image: '/images/themes/stockist/unique/pic-3.webp',
        alt: 'Scroll-Activated Storytelling',
        title: 'Scroll-Activated Storytelling',
        text: 'Create a refined storytelling moment — text gently fades in with scrolling, adding depth and elegance to your layout without overwhelming the visuals.',
    },
    {
        image: '/images/themes/stockist/unique/pic-4.webp',
        alt: 'Tabbed Content with Modal Visuals',
        title: 'Tabbed Content with Modal Visuals',
        text: 'Engage users with tabs that open modal windows on click, combining organized content and smooth scrolling visuals for an immersive experience.',
    },
    {
        image: '/images/themes/stockist/unique/pic-5.webp',
        video: '/images/themes/stockist/animation.mp4',
        alt: 'Scroll-Triggered Video Section',
        title: 'Scroll-Triggered Video Section',
        text: 'Capture attention with a sleek video section featuring a smooth scroll-activate effect and intuitive playback controls.',
    },
    {
        image: '/images/themes/stockist/unique/pic-6.webp',
        alt: 'Interactive Collection Grid',
        title: 'Interactive Collection Grid',
        text: 'Draw shoppers in with an interactive experience — collections scale up on hover, making discovery more impactful and memorable.',
    },
];

const UniqueItem: React.FC<UniqueItemProps> = ({ image, alt, title, text, video }) => (
    <div className={styles.uniqueSection__item}>
        <div className={styles.uniqueSection__pic}>
            {video ? (
                <video
                    src={video}
                    autoPlay
                    playsInline
                    muted
                    loop
                />
            ) : (
                <NextImage
                    src={image}
                    alt={alt}
                    fill
                    sizes="(max-width: 480px) 100vw, (max-width: 1280px) 50vw, 75vw"
                    loading="lazy"
                />
            )}
        </div>
        <div className={styles.uniqueSection__about}>
            <h4 className={styles.uniqueSection__title}>{title}</h4>
            <div className={styles.uniqueSection__text}>{text}</div>
        </div>
    </div>
);

const UniqueSection = () => {

    const {ref: inViewRef2, inView: inView2} = useInView({
        triggerOnce: true,
        rootMargin: '-250px 0px 0px',
        threshold: 0,
    });

    return (
        <section className={styles.uniqueSection}>
            <Wrapper>
                <div className={styles.uniqueSection__head} ref={inViewRef2}>
                    <Label>Unique Sections</Label>
                    <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
                        {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
                    </h2>
                    <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
                        {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
                    </p>
                </div>

                <div className={styles.uniqueSection__grid}>
                    {items.map((item, index) => (
                        <UniqueItem key={index} {...item} />
                    ))}
                </div>
            </Wrapper>
        </section>
    );
};

export default UniqueSection;
